/**
* Licensed Materials - Property of IBM
* IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2022
* US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
*/
define([
	'underscore',
	'../../app/util/GlassUtil',
	'text!../schema/schemaDefs.json',
	'text!../schema/schemaMappings.json',
	'text!../schema/nameSchema.json',
	'text!../schema/layoutSchema.json',
	'text!../schema/layoutFragmentSchema.json',
	'text!../schema/themeSchema.json',
	'text!../schema/versionSchema.json',
	'text!../schema/eventGroupsSchema.json',
	'text!../schema/drillThroughSchema.json',
	'text!../schema/propertiesSchema.json',
	'text!../schema/widgetsSchema.json',
	'text!../schema/widgetsFragmentSchema.json',
	'text!../schema/widgetsTemplateSchema.json',
	'jquery'
], function(_, GlassUtil, SchemaDefs, SchemaMappings, NameSchema, LayoutSchema, LayoutFragmentSchema, ThemeSchema, VersionSchema,
	EventGroupsSchema, DrillThroughSchema, PropertiesSchema, WidgetsSchema, WidgetsFragmentSchema, WidgetsTemplateSchema, $) {

	const findObj = (ary, id) =>  _.find(ary, (ele) => id === ele.id);

	class DashboardSpecHelper {

		/**
		 * Gets the spec content of a selected widget
		 * @param {object} controller			- the interaction controller (either a interaction/Controller or interaction/BaseController
		 * from which the spec content is derived
		 */
		constructor(controller) {
			// TODO lifecycle_cleanup -- removethe controller dependency here
			this.controller = controller;
			this.dashboardApi = controller.dashboardApi;
			this.glassContext = controller.glassContext;
			this.logger = (this.glassContext &&  this.glassContext.getCoreSvc) ? this.glassContext.getCoreSvc('.Logger') : null;
		}

		/**
		 * Gets the spec content of a widget
		 * @param {boolean} pinning	- indicates if the call was triggered from pinning. If so, get more content
		 * @param {ContentAPI} content - ContentApi object to get the contentSpec from, if undefined, selected contents will be used instead
		 * @returns returns the spec content for each selected node as an array element
		 */
		getContents(pinning, content) {
			const specContents = [];
			let selectedContent = [];
			const internalAPI = this.dashboardApi.getFeature('internal');
			const boardModel = internalAPI.getBoardModel();
			const specVersion = boardModel.version;
			const canvas = this.dashboardApi.getCanvas();

			if (content) {
				selectedContent = [content];
			}else {
				//Use legacy approach to get selected contents, find CADBC-2481
				//TODO: POST 11.2.0, it should call getSelectedContentList()
				const contentIds = this.controller.getSelectedNodes().map(node=>node.id);
				let contentFromId;
				contentIds.forEach((id)=> {
					contentFromId = canvas.getContent(id);
					if(contentFromId){
						selectedContent.push(contentFromId);
					}
				});
			}
			selectedContent.forEach((selectedContent) => {
				const layoutModel = selectedContent.getFeature('Models.internal').getLayoutModel();
				const layoutWidgetIds = layoutModel.listWidgets([layoutModel.id]);
				const layoutWidgets = [];
				const nonMergedWidgets = [];
				const timelineEpisodes = [];
				const sourceIds = [];
				let errMsg;

				// ALERT! WARNING! DANGER!  `widgetModel` is set in the loop below, and the last-instance of it is consumed
				// when setting specContent.drillThrough. Screwy javascript scoping rules for `var` types hid this,
				// but it's almost certianly a bug in some context?
				let widgetModel;
				for (let j = 0; j < layoutWidgetIds.length; j++) {
					const widgetContent = canvas.getContent(layoutWidgetIds[j]) || selectedContent.getContent(layoutWidgetIds[j]) || selectedContent;
					const internalModels = widgetContent.getFeature('Models.internal');
					widgetModel = internalModels.getWidgetModel();
					const mergedModel = this._addFilterToWidgetModel(widgetModel);
					// Copy scenario, but don't copy filter widgets since filters are not supported
					if(!pinning) {
						if(widgetModel.type !== 'filter') {
							nonMergedWidgets.push(widgetModel);
							layoutWidgets.push(mergedModel);
						} else {
							errMsg = 'filterWidgetCopyWarn';
						}
					} else {
						layoutWidgets.push(mergedModel);
					}

					const timelineEpisodeForPinning = this._getTimeLineEpisodeForPinning(boardModel, layoutWidgetIds[j]);
					if (timelineEpisodeForPinning) {
						timelineEpisodes.push(timelineEpisodeForPinning);
					}

					const viz = widgetContent.getFeature('Visualization');
					if (viz){
						const dataSource = viz.getDataSource();
						const sourceId = dataSource ? dataSource.getId(): null;
						if (sourceId && sourceIds.indexOf(sourceId) === -1) {
							sourceIds.push(sourceId);
						}
					}
				}

				const dataSources = this._getDataSources(sourceIds, boardModel.dataSources);

				const specContent = {
					specVersion: specVersion,
					layout: layoutModel,
					widgets: layoutWidgets,
					dataSources: dataSources,
					dashboardID: '',
					errMsg: errMsg,
					nodeCopied: true
				};

				if(errMsg) {
					specContent.nodeCopied = false;
				}

				// make this consistent with specContent.widgets
				specContent.nonMergedWidgets = nonMergedWidgets;

				if (selectedContent.getContainer()) {
					specContent.parentID = selectedContent.getContainer().getId();
				}
				// widgetModel can be absent in a case where a layout content (with no child widget) is selected
				// although this is not possible with a group, but an inner tab (aka tab widget) can exist without
				// any widgets added to the layout
				if (widgetModel && boardModel.drillThrough && typeof boardModel.drillThrough.getModels === 'function') {
					const drillThroughService = this.dashboardApi.getDashboardCoreSvc('DrillThroughService');
					// ALERT! WARNING! DANGER! (see above)
					specContent.drillThrough = drillThroughService.getDrillThroughByWidgetAndGroups(widgetModel, pinning);
				}

				if (boardModel.properties && boardModel.properties.customColors) {
					specContent.properties = {
						customColors: {
							colors: boardModel.properties.customColors.colors
						}
					};
				}


				if (boardModel.fredIsRed) {
					specContent.fredIsRed = boardModel.fredIsRed;
				}

				// TODO: instead of stripping out a known attribute, we should ignore anything runtime/transient attribute (eg. runtimeOnly flag) in the model.
				// When explicitScale is set to truthy, the VIDA normalization is not set; hence we does not want this property
				if(layoutModel.content && layoutModel.content.properties) {
					const contentProperties = Object.keys(layoutModel.content && layoutModel.content.properties);
					if(contentProperties.includes('explicitScale')){
						delete specContent.layout.content.properties['explicitScale'];
					}
				}

				// Time Line model exists
				if (timelineEpisodes.length > 0) {
					specContent.episodes = timelineEpisodes;
				}

				specContents.push(specContent);

			});
			return specContents;
		}

		/**
		 * Gets all the filters applied on the widget
		 * @param {Object} widgetModel - the widget associated inside the spec
		 * @returns returns the widget obj adding the filters if any
		 */
		_addFilterToWidgetModel(widgetModel) {
			const content = this.dashboardApi.getCanvas().getContent(widgetModel.id);
			const legacyVisualization = content && content.getFeature('Visualization.legacy');
			if (legacyVisualization) {
				let widgetModelClone = $.extend(true, {}, widgetModel);
				let portableFilters = legacyVisualization.getLegacyManagers().visFilterSupport.getAllFiltersAsLocalFiltersForPinning();
				if (portableFilters) {
					widgetModelClone.localFilters = portableFilters;
					delete widgetModelClone.filters;
				}
				return widgetModelClone;
			} else {
				return widgetModel;
			}

		}

		_removeModelsInternal(layout) {
			if (layout.content && layout.content.features) {
				delete layout.content.features['Models_internal'];
				if (Object.keys(layout.content.features).length === 0) {
					delete layout.content.features;
				}
				if (Object.keys(layout.content).length === 0) {
					delete layout.content;
				}
			}
			if (layout.items) {
				for (let j = 0; j < layout.items.length; j++) {
					this._removeModelsInternal(layout.items[j]);
				}
			}
		}

		/**
		 * Gets the spec content of a widget as JSON, it assumes that no pinning content is needed
		 * @param {ContentAPI} content (Optional - if undefined, selected content will be used instead)
		 * @returns {Object} Returns an object which contains the spec content represented as JSON, use property `spec` to access the spec JSON string
		 *
		 */
		getContentsToJSONSpec(content) {
			const JSONcontent = {};
			let JSONSpec;
			let specContents = this.getContents(false, content);

			let assets = _.countBy(specContents, (content) => { return content.nodeCopied ? 'copied' : 'notCopied'; });

			specContents = this._removeErrorsFromSpec(specContents, JSONcontent);

			if (specContents.length) {
				if (specContents[0].specVersion) {
					JSONcontent.specVersion = specContents[0].specVersion;
				}
				JSONcontent.layout = specContents.map( (specContent) => {
					const jsonContentLayout = specContent.layout.toJSON();
					this._removeModelsInternal(jsonContentLayout);
					return jsonContentLayout;
				});
				JSONcontent.parentID = _.map(specContents, (content) => { return content.parentID; });
				JSONcontent.dashboardID = specContents[0].dashboardID;

				JSONcontent.widgets = _.chain(specContents)
					.map((content) => { return content.widgets; })
					.flatten()
					.map((widget) => { return widget.toJSON(); })
					.value();

				JSONcontent.nonMergedWidgets = _.chain(specContents)
					.map((content) => { return content.nonMergedWidgets; })
					.flatten()
					.map((widget) => { return widget.toJSON(); })
					.value();

				JSONcontent.dataSources = {};
				JSONcontent.dataSources.version = specContents[0].dataSources.version;
				JSONcontent.dataSources.sources = [];
				let resultDrillThroughDefinitions = [];
				_.each(specContents, function(content) {
					_.each (content.dataSources.sources, function (source) {
						let idx = -1;
						// if the dataSource is already there, don't add it again
						if ( JSONcontent.dataSources.sources.length > 0) {
							idx = JSONcontent.dataSources.sources.findIndex(function(ele) {
								return ele.id === source.id;
							});
						}
						if (idx < 0) {
							const sourceJSON = source.toJSON();
							// if the datasource is saved as an embedded module, the embedded module is saved
							// as an attachment to the original dashboard object in CM. Sharing the embedded module
							// can lead to side effects which was not intended from the original dashboard, which
							// leads to a stale datasource.
							// Remove the embeddedModuleId so that the target dashboard does not share the embedded module
							// with the source dashboard, but rather builds its own embedded module.
							if (sourceJSON && sourceJSON.shaping && sourceJSON.shaping.embeddedModuleId) {
								delete sourceJSON.shaping.embeddedModuleId;
								delete sourceJSON.shaping.embeddedModuleName;
								sourceJSON.shaping.embeddedModuleUpToDate = false;
							}
							JSONcontent.dataSources.sources.push(sourceJSON);
						}
					});

					// Add drill through definitions from selected widget without duplicates.
					if (content.drillThrough && content.drillThrough.length) {
						content.drillThrough.forEach((drillDefinition) => {
							let definitionExists = _.find(resultDrillThroughDefinitions, (resultDefinition) => {
								return resultDefinition.id === drillDefinition.id;
							});
							if (!definitionExists) {
								resultDrillThroughDefinitions.push(drillDefinition.toJSON());
							}
						});
					}

				});

				if (specContents[0].properties) {
					JSONcontent.properties = specContents[0].properties;
				}

				if (specContents[0].fredIsRed) {
					JSONcontent.fredIsRed = specContents[0].fredIsRed;
				}

				if (resultDrillThroughDefinitions.length) {
					JSONcontent.drillThrough = resultDrillThroughDefinitions;
				}

				const episodes = _.chain(specContents)
					.map((content) => { return content.episodes; })
					.flatten()
					.filter(Boolean)
					.map((episode) => { return episode.toJSON(); })
					.value();

				if(episodes.length) {
					JSONcontent.episodes = episodes;
				}
			}
			if (JSONcontent) {
				JSONSpec = JSON.stringify(JSONcontent, null, 4);
			}
			return {
				spec: JSONSpec,
				count: assets.copied,
				errMsg: JSONcontent.errMsg
			};
		}

		/**
		 * Validates a dashboard JSON spec
		 * @param {string} spec			- spec to validate
		 * @param {array} retErrors		- (optional), if present, all errors are added to it and not shown in the Dashboard.
		 * 								The object is {schema,schemaCondition,schemaKeyword,schemaDataPath,data,msg};
		 * @returns returns the JSON objects of the valid spec, otherwise null if validation fails
		 */
		validateDashboardSpec(spec, retErrors) {
			return Promise.resolve().then(() => {

				let defaultSchema = 'noSchema';
				let defaultSchemaCondition = 'noSchemaCondition';
				let defaultSchemaKeyword = 'noSchemaKeyword';
				let defaultSchemaDataPath = 'noSchemaDataPath';
				let defaultData = 'noData';
				let defaultMsg = 'noMsg';
				let errorObj = {
					'schema': defaultSchema,
					'schemaCondition': defaultSchemaCondition,
					'schemaKeyword': defaultSchemaKeyword,
					'schemaDataPath': defaultSchemaDataPath,
					'data': defaultData,
					'msg': defaultMsg
				};
				let outSpec;

				try {
					outSpec = JSON.parse(spec);
				} catch (err) {
					let msg = 'Invalid JSON provided';
					if (this.logger) {
						this.logger.error(msg, err, this);
					}

					if (retErrors !== undefined && retErrors !== null && _.isArray(retErrors)) {
						let errObj = Object.create(errorObj);
						errObj.data = err;
						errObj.msg = msg;
						retErrors.push(
							errObj
						);
						return null;
					} else {
						throw new Error('invalidJSONResponse');
					}
				}

				// finds a widget
				function findWidget(id, type, widgetIds) {
					if (type === 'widget') {
						let widget = findObj(outSpec.widgets, id);
						if (!widget) {
							// if widget can not be found, save layout id
							widgetIds.push(id);
						}
					}
				}

				// recursively finds widgets
				function findWidgets (items, widgetIds) {
					_.each(items, (item) => item.items ? findWidgets(item.items, widgetIds) : findWidget(item.id, item.type, widgetIds));
				}

				let errors = [];

				// check for empty spec
				let specFragment = false;
				if (!outSpec || Object.keys(outSpec).length === undefined || Object.keys(outSpec).length === 0) {
					// signal error
					let msg = 'Spec is empty';
					if (retErrors !== undefined && retErrors !== null && _.isArray(retErrors)) {
						let errObj = Object.create(errorObj);
						errObj.msg = msg;
						retErrors.push(
							errObj
						);
						return null;
					} else {
						throw new Error(msg);
					}
				} else if (Array.isArray(outSpec.layout)) {
					// spec fragment since the only other choice is an array
					specFragment = true;
				}

				// if in production, do not validate the spec against schemas
				if (!GlassUtil.isDevMode(this.glassContext)) {
					return outSpec;
				}

				return this._getAjv().then((ajv) => {
					// use key/value pair to determine which schema to use with a spec key
					let shmaMappings = JSON.parse(SchemaMappings);

					// validate all of the spec, accumulate errors if any
					Object.keys(outSpec).forEach((specKey) => {

						let keySchema = shmaMappings[specKey];
						if (specFragment && specKey === 'layout') {
							keySchema = shmaMappings['layoutFragment'];
						}
						if (specFragment && specKey === 'widgets') {
							keySchema = shmaMappings['widgetsFragment'];
						}

						let specValue = outSpec[specKey];
						if (keySchema) {
							let validate = ajv.getSchema(keySchema);
							if (validate) {
								if (!validate(specValue)) {
									// each call to validate destroys any previous errors so much save them
									validate.errors.forEach( function(error) {
										let errObj = Object.create(errorObj);
										errObj.schema = error.schemaPath;
										errObj.schemaCondition = error.parentSchema;
										errObj.schemaKeyword = error.keyword;
										errObj.schemaDataPath = error.dataPath;
										errObj.data = error.data;
										errObj.msg = error.message;

										errors.push(errObj);
									});
								}
							}
						}
						// NOTE: for now leave this commented out since we don't have schemas for all spec keys
						// else {
						// 	generate error, invalid key
						// 	errors.push( {
						// 		'msg': 'Spec Key: ' + specKey + ' does not exists in schema mapping definition'
						// 	});
						// }
					});

					// semantic checks
					// 1. check widget id in layout to make sure there is a widget in the spec for it. If any widget ids are returned,
					// then there is no widget object for it
					let widgetIds = [];
					if (outSpec.widgets) {
						if (outSpec.layout.items) {
							findWidgets(outSpec.layout.items, widgetIds);
						} else {
							findWidgets(outSpec.layout, widgetIds);
						}
						if (widgetIds.length) {
							let errObj = Object.create(errorObj);
							errObj.schema = defaultSchema;
							errObj.schemaDataPath = defaultSchemaDataPath;
							errObj.schemaKeyword = defaultSchemaKeyword;
							errObj.data = defaultData;
							errObj.schemaCondition = 'semantic layout widget id check';
							errObj.msg = 'No widget objects in the specification for layout ids ' + widgetIds.toString();
							errors.push(errObj);
						}
					}

					if (errors.length) {
						outSpec = null;
						if (retErrors !== undefined && retErrors !== null && _.isArray(retErrors)) {
							retErrors.push(errors);
						} else {
							// log all errors
							if (this.logger) {
								_.each(errors, (err) => {
									let msg;
									if (err.msg !== defaultMsg) {
										msg = ' error: ' + err.msg;
									}
									if (err.data !== defaultData) {
										msg += ' data: ' + err.data;
									}
									if (err.schema !== defaultSchema) {
										msg += ' schema: ' + err.schema;
									}
									if (err.schemaCondition !== defaultSchemaCondition) {
										msg += ' schema defn: ' + err.schemaCondition.$id;
									}
									this.logger.error('schema validation error:', msg, this);
								});
							}
							throw new Error('schema validation errors logged above');
						}
					}
					return outSpec;
				});
			});
		}

		_getAjv() {
			if ( this.ajv ) {
				return Promise.resolve(this.ajv);
			} else {
				return new Promise((resolve, reject) => {
					require(['ajv'], (Ajv) => {
						try {
							let shmaDefs = JSON.parse(SchemaDefs);
							// let shma = JSON.parse(Schema);
							let nameShma = JSON.parse(NameSchema);
							let layoutSchema = this._getLayoutSchema();
							let layoutFragmentShma = JSON.parse(LayoutFragmentSchema);
							let themeShma = JSON.parse(ThemeSchema);
							let versionShma = JSON.parse(VersionSchema);
							let evtGrpsShma = JSON.parse(EventGroupsSchema);
							let drillThroughShma = JSON.parse(DrillThroughSchema);
							let propertiesShma = JSON.parse(PropertiesSchema);
							let widgetsShma = JSON.parse(WidgetsSchema);
							let widgetsFragmentShma = JSON.parse(WidgetsFragmentSchema);
							let widgetsTemplateSchema = JSON.parse(WidgetsTemplateSchema);
							this.ajv = new Ajv( { allErrors: true, verbose: true, schemas:[shmaDefs, nameShma, layoutSchema, layoutFragmentShma, themeShma, versionShma, evtGrpsShma, drillThroughShma, propertiesShma, widgetsShma, widgetsFragmentShma, widgetsTemplateSchema ] } );
							resolve(this.ajv);
						} catch ( err ) {
							reject(err);
						}
					}, reject);
				});
			}
		}

		/**
		 * get layout schema, it will add the registered types to the layoutType.enum
		 * @returns the layout schema
		 */
		_getLayoutSchema() {
			const layoutSchema = JSON.parse(LayoutSchema);
			const contentTypeRegistry = this.dashboardApi.getFeature('ContentTypeRegistry');
			const layoutTypeEnum = layoutSchema.definitions.layoutType.enum;
			layoutSchema.definitions.layoutType.enum = layoutTypeEnum.concat(contentTypeRegistry.getRegisteredTypes());
			return layoutSchema;
		}

		/**
		 * Creates an datasource spec object
		 * @param {array} sourceIds			- array of data source ids
		 * @param {object} dataSources		- array of datasources
		 * @returns returns an object containing the datasource version and source objects
		 */
		_getDataSources(sourceIds, dataSources) {
			const copiedInfo = {
				version: '0.0',
				sources: []
			};

			if (dataSources && typeof dataSources === 'object') {
				copiedInfo.version = dataSources.version;
				const sources = copiedInfo.sources;
				sourceIds.forEach((id) => {
					sources.push(dataSources.get('sources').get(id));
				});
			}
			return copiedInfo;
		}

		/**
		 * @param boardModel the boardModel
		 * @param layoutWidgetId The layout id for the widget being processed.
		 * @returns the timelineEpisode for widget which is attached to the pin for this layoutWidgetId. or null if there is none.
		 *			The model is returned from the board spec.
		 *
		 */
		_getTimeLineEpisodeForPinning (boardModel, layoutWidgetId) {
			if( boardModel.timeline && typeof this.controller.boardModel.timeline.get === 'function' ){
				return boardModel.timeline.episodes.get(layoutWidgetId);
			}
			return null;
		}

		/**
		 * checks spec contents object for filter widget (denoted by errMsg being defined) and removes that element from the array
		 * @param {array} specContents - content objects
		 * @param {object} jsonContent - content to be converted to json object
		 */
		_removeErrorsFromSpec(specContents, jsonContent) {
			jsonContent.errMsg = _.first(_.compact(_.map(specContents, (content) => { return content.errMsg;})));

			if(jsonContent.errMsg) {
				// keep the widget(s) that are not in error
				return _.filter( specContents, (content) => {
					return content.errMsg === undefined;
				});
			}
			return specContents;
		}
	}

	return DashboardSpecHelper;
});
